import React from "react";
import { SensorDataFile, SensorDataSessionItemList, SensorDataSessionMarkerDot, Station } from "@shared/models";
import { TableProperty } from "@shared/components/Table/interfaces";
import { getFormattedDate } from "@shared/utils";
import { Icon } from "@shared/components";

export const sensorDataSessionTableProperties: TableProperty<SensorDataSessionItemList>[] = [
  {
    title: "Name",
    key: "name",
    get: (item) => item.name,
  },
  {
    title: "Date Created",
    key: "created_at",
    get: (item) => getFormattedDate(item.created_at),
  },
  {
    title: "Created By",
    key: "created_by",
    get: (item) => item.created_by,
  },
  {
    title: "Amount of Files",
    key: "files_count",
    get: (item) => item.files_count,
  },
];

export const getSensorDataSessionDotsFilesTableProperties = (
  items: SensorDataSessionMarkerDot[],
  files: SensorDataFile[],
  onDeleteDot: (dot: SensorDataSessionMarkerDot) => void,
): TableProperty<SensorDataSessionMarkerDot>[] => {
  const filesKeyName = files.reduce((r: { [key: string]: string }, i) => {
    r[String(i.id)] = i.custom_name;
    return r;
  }, {});

  return [
    {
      title: "File Name",
      key: "created_at",
      get: (item: SensorDataSessionMarkerDot) => (item.session_file_id ? filesKeyName[item.session_file_id] : "-"),
    },
    {
      title: "Timestamp",
      key: "time",
      get: (item: SensorDataSessionMarkerDot) => item.time,
    },
    {
      title: "Sample Number",
      key: "number",
      get: (item: SensorDataSessionMarkerDot) => item.number,
    },
    {
      title: "File Row Number",
      key: "row_number",
      get: (item: SensorDataSessionMarkerDot) => item.row_number,
    },
    {
      title: "Value",
      key: "value",
      get: (item: SensorDataSessionMarkerDot) => item.value,
    },
    {
      title: "Millisec Dif",
      key: "seconds",
      get: (item: SensorDataSessionMarkerDot, index?: string | number | undefined) => {
        const prevItem = items[Number(index || 0) - 1];
        if (!prevItem || prevItem.session_file_id !== item.session_file_id) return "-";
        return (item.seconds - prevItem.seconds).toFixed(5);
      },
    },
    {
      title: "Rate",
      key: "resolution",
      get: (item: SensorDataSessionMarkerDot) => {
        const file = files.find((f) => f.id === item.session_file_id);
        if (!file) return "-";

        return file.rate;
      },
    },
    {
      title: "",
      key: "created_by",
      cellClick: (item) => onDeleteDot(item),
      get: () => "",
      cellClickBlock: (
        <div className="delete-column">
          <Icon type="delete" />
          <span>Remove</span>
        </div>
      ),
    },
  ];
};

export const getSensorDataSessionDotsStationsTableProperties = (
  items: SensorDataSessionMarkerDot[],
  stations: Partial<Station>[],
  onDeleteDot: (dot: SensorDataSessionMarkerDot) => void,
): TableProperty<SensorDataSessionMarkerDot>[] => {
  const stationsKeyName = stations.reduce((r: { [key: string]: string }, i) => {
    r[String(i.station_code)] = String(i.station_code);
    return r;
  }, {});

  return [
    {
      title: "SDH Code",
      key: "created_at",
      get: (item: SensorDataSessionMarkerDot) => (item.session_station ? stationsKeyName[item.session_station] : "-"),
    },
    {
      title: "Timestamp",
      key: "time",
      get: (item: SensorDataSessionMarkerDot) => item.time,
    },
    {
      title: "Sample Number",
      key: "number",
      get: (item: SensorDataSessionMarkerDot) => item.number,
    },
    {
      title: "Value",
      key: "value",
      get: (item: SensorDataSessionMarkerDot) => item.value,
    },
    {
      title: "Millisec Dif",
      key: "seconds",
      get: (item: SensorDataSessionMarkerDot, index?: string | number | undefined) => {
        const prevItem = items[Number(index || 0) - 1];
        if (!prevItem || prevItem.session_station !== item.session_station) return "-";
        return Math.abs((item.seconds - prevItem.seconds) / 1000).toFixed(5);
      },
    },
    {
      title: "",
      key: "created_by",
      cellClick: (item) => onDeleteDot(item),
      get: () => "",
      cellClickBlock: (
        <div className="delete-column">
          <Icon type="delete" />
          <span>Remove</span>
        </div>
      ),
    },
  ];
};
